/**
 * ops2026 — Leads (founder request 2026-09-25).
 *
 * Every email + domain entered into a Sill signup or readiness-report
 * form, whether it became an account ("claimed"), and when the domain was
 * scanned. A second list shows domains scanned on sill.so or before signup that no
 * captured email refers to. Filter by claimed status, search, export CSV.
 *
 * Emails were only captured from 2026-09-25 onward (earlier readiness
 * reports stored a hash only), plus a backfill of the signup links that
 * still existed.
 */
import { ChevronLeft, ChevronRight, Download, Link2, Mail, RefreshCw, Trash2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';

import type {
  AdminContactStatus,
  AdminLead,
  AdminLeadsResponse,
  AdminPublicScanSummary,
  AdminScannedDomain,
  AdminSendReportResult,
} from '@sill/types/api';

import { useAdminApi } from '../../api/api-context.js';
import { ApiError } from '../../api/http-client.js';
import { Card } from '../../components/atoms/Card.js';
import { ConfirmDialog } from '../../components/atoms/ConfirmDialog.js';
import { Pill } from '../../components/atoms/Pill.js';
import { TableScroll } from '../../components/atoms/TableScroll.js';
import type { SetView } from '../../app/view-router.js';
import { formatIsoCompact } from '../../lib/format.js';
import { useC } from '../../lib/theme.js';

const serifFont = "'Instrument Serif', serif";
const sansFont = "'Geist', sans-serif";
const monoFont = "'JetBrains Mono', monospace";

type Status = 'all' | 'claimed' | 'unclaimed';
type Tab = 'emails' | 'domains';

/** Per-signal columns in the domains CSV, in the sill.so scan's order. */
const CSV_SIGNALS = [
  'agent_card', 'mcp', 'ai_access', 'content_signals', 'llms_txt',
  'structured_data', 'api_catalog', 'sitemap', 'robots_txt', 'crawler_access',
  'sill_installed',
  // ADR-0225 — informational x402 demand signal (closed enums).
  'x402', 'x402_kind',
] as const;

const MAX_REPORT_LINKS = 25;
const BLOCKING: readonly AdminContactStatus[] = ['hard_bounce', 'unsubscribed', 'complaint'];

const CONTACT_TONE: Record<AdminContactStatus, 'lime' | 'amber' | 'coral' | 'neutral'> = {
  active: 'lime',
  soft_bounce: 'amber',
  hard_bounce: 'coral',
  unsubscribed: 'neutral',
  complaint: 'coral',
};

/** One line for the flash after a staff send. */
function sendResultLine(r: AdminSendReportResult, email: string, domain: string): { ok: boolean; text: string } {
  switch (r.kind) {
    case 'sent': return { ok: true, text: `Report for ${domain} sent to ${email}.` };
    case 'blocked': return { ok: false, text: `Not sent: ${email} is ${r.contact_status.replace('_', ' ')}.` };
    case 'deduped': return { ok: false, text: `Not sent: ${email} already got the ${domain} report in the last 24 hours.` };
    case 'breaker': return { ok: false, text: 'Not sent: the hourly report email cap is reached. Try again later.' };
    case 'unreachable': return { ok: false, text: `Not sent: ${domain} could not be read, so there is no report to send.` };
    case 'postal_address_missing': return { ok: false, text: 'Not sent: no postal address is configured for outreach email.' };
    case 'invalid': return { ok: false, text: 'Not sent: check the email address and domain.' };
    case 'disabled': return { ok: false, text: 'Not sent: report email is turned off.' };
    case 'failed': return { ok: false, text: 'Not sent: the send failed. Try again.' };
  }
}

/** "3 of 8" from the visitor's last sill.so scan; "—" when the domain was
 *  only scanned at email capture. */
function readinessLabel(s: AdminPublicScanSummary | null): string {
  if (s === null) return '—';
  if (s.signals.sill_installed === 'present') return 'Sill installed';
  if (s.outcome !== 'checked') return 'Could not read';
  return `${s.signals_present} of ${s.signals_total}`;
}

function signalsTitle(s: AdminPublicScanSummary | null): string | undefined {
  if (s === null) return undefined;
  return CSV_SIGNALS.map((k) => `${k}: ${s.signals[k] ?? 'n/a'}`).join('\n');
}

const PAGE_SIZE = 25;

const leadKey = (l: { email: string; domain: string | null }): string => `${l.email}|${l.domain ?? ''}`;

function csvCell(v: string | number | boolean | null): string {
  const s = v === null ? '' : String(v);
  // Neutralise spreadsheet formula injection and quote every cell.
  const safe = /^[=+\-@\t\r]/.test(s) ? `'${s}` : s;
  return `"${safe.replace(/"/g, '""')}"`;
}

function downloadCsv(filename: string, rows: (string | number | boolean | null)[][]): void {
  const text = rows.map((r) => r.map(csvCell).join(',')).join('\r\n');
  const blob = new Blob([text], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function dateOnly(iso: string | null): string {
  return iso === null ? '—' : formatIsoCompact(iso).slice(0, 10);
}

export function LeadsView({ setView }: { setView: SetView }): JSX.Element {
  const C = useC();
  const api = useAdminApi();
  const [status, setStatus] = useState<Status>('unclaimed');
  const [tab, setTab] = useState<Tab>('emails');
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [offset, setOffset] = useState(0);
  const [data, setData] = useState<AdminLeadsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [flash, setFlash] = useState<string | null>(null);
  const [sendTarget, setSendTarget] = useState<{ email: string; domain: string; editable: boolean } | null>(null);
  const [sendEmail, setSendEmail] = useState('');
  const [sending, setSending] = useState(false);
  const [linking, setLinking] = useState(false);

  // Search as you type (debounced), like the accounts list.
  useEffect(() => {
    const t = setTimeout(() => setQuery(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Any filter change starts from page one with nothing selected.
  useEffect(() => {
    setOffset(0);
    setSelected(new Set());
  }, [tab, status, query]);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      setData(await api.listLeads({
        status,
        q: query,
        limit: PAGE_SIZE,
        offset,
        kind: tab === 'emails' ? 'leads' : 'domains',
      }));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load leads.');
    } finally {
      setLoading(false);
    }
  }, [api, status, query, offset, tab]);

  useEffect(() => {
    void load();
  }, [load]);

  const toggle = (key: string): void => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const deleteSelected = async (): Promise<void> => {
    if (data === null || selected.size === 0) return;
    setDeleting(true);
    try {
      const res = tab === 'emails'
        ? await api.deleteLeads({
            leads: data.leads
              .filter((l) => selected.has(leadKey(l)))
              .map((l) => ({ email: l.email, domain: l.domain })),
          })
        : await api.deleteLeads({ domains: data.scanned_domains.filter((d) => selected.has(d.domain)).map((d) => d.domain) });
      setFlash(
        tab === 'emails'
          ? `Deleted ${selected.size} lead${selected.size === 1 ? '' : 's'}.`
          : `Deleted ${res.scan_rows_deleted} scan${res.scan_rows_deleted === 1 ? '' : 's'} for ${selected.size} domain${selected.size === 1 ? '' : 's'}.`,
      );
      setSelected(new Set());
      setConfirmOpen(false);
      await load();
    } catch (err) {
      setError(err instanceof ApiError && err.kind === 'forbidden' ? 'Superadmin required to delete leads.' : 'Delete failed. Nothing was removed; retry.');
      setConfirmOpen(false);
    } finally {
      setDeleting(false);
    }
  };

  useEffect(() => {
    if (flash === null) return;
    const t = setTimeout(() => setFlash(null), 5000);
    return () => clearTimeout(t);
  }, [flash]);

  const openSend = (email: string, domain: string, editable: boolean): void => {
    setSendEmail(email);
    setSendTarget({ email, domain, editable });
  };

  const sendReport = async (): Promise<void> => {
    if (sendTarget === null) return;
    const email = sendEmail.trim();
    setSending(true);
    try {
      const r = await api.sendLeadReport({ email, domain: sendTarget.domain });
      const line = sendResultLine(r, email, sendTarget.domain);
      if (line.ok) setFlash(line.text);
      else setError(line.text);
      setSendTarget(null);
      if (line.ok) void load();
    } catch (err) {
      setError(err instanceof ApiError && err.kind === 'forbidden' ? 'Superadmin required to send reports.' : 'Send failed. Try again.');
    } finally {
      setSending(false);
    }
  };

  /** Domains behind the current selection (both tabs). */
  const selectedDomains = (): string[] => {
    if (data === null) return [];
    const ds = tab === 'emails'
      ? data.leads.filter((l) => selected.has(leadKey(l)) && l.domain !== null).map((l) => l.domain as string)
      : data.scanned_domains.filter((d) => selected.has(d.domain)).map((d) => d.domain);
    return [...new Set(ds)];
  };

  const createLinks = async (): Promise<void> => {
    const domains = selectedDomains();
    if (domains.length === 0) return;
    if (domains.length > MAX_REPORT_LINKS) {
      setError(`Select at most ${MAX_REPORT_LINKS} domains per batch.`);
      return;
    }
    setLinking(true);
    setError(null);
    try {
      const res = await api.createReportLinks({ domains });
      const ready = res.results.filter((r) => r.report_url !== null).length;
      const unreadable = res.results.filter((r) => r.kind === 'unreachable').length;
      setFlash(`Report links ready for ${ready} of ${res.results.length} domains${unreadable > 0 ? ` (${unreadable} could not be read)` : ''}.`);
      void load();
    } catch (err) {
      setError(err instanceof ApiError && err.kind === 'forbidden' ? 'Superadmin required.' : 'Could not create report links. Try again.');
    } finally {
      setLinking(false);
    }
  };

  // Export every row matching the current filters, not just this page.
  const exportCsv = async (): Promise<void> => {
    const kind = tab === 'emails' ? 'leads' : 'domains';
    const all: AdminLeadsResponse[] = [];
    for (let off = 0; ; off += 500) {
      const page = await api.listLeads({ status, q: query, limit: 500, offset: off, kind });
      all.push(page);
      const total = kind === 'leads' ? page.leads_total : page.scanned_domains_total;
      if (off + 500 >= total) break;
    }
    if (tab === 'emails') {
      downloadCsv(`sill-leads-${status}.csv`, [
        ['email', 'domain', 'sources', 'first_seen', 'last_seen', 'claimed', 'account', 'claimed_at', 'last_scan', 'scans',
          'email_status', 'last_staff_send', 'report_url'],
        ...all.flatMap((p) => p.leads).map((l) => [
          l.email, l.domain, l.sources.join(' '), l.first_seen_at, l.last_seen_at,
          l.claimed, l.claimed_account_name, l.claimed_at, l.last_scan_at, l.scan_count,
          l.contact_status, l.last_staff_send_at, l.report_url,
        ]),
      ]);
    } else {
      downloadCsv(`sill-scanned-domains-${status}.csv`, [
        ['domain', 'scans', 'first_scan', 'last_scan', 'claimed', 'account', 'report_url',
          'scan_outcome', 'platform', 'signals_live', 'signals_total', ...CSV_SIGNALS],
        ...all.flatMap((p) => p.scanned_domains).map((d) => [
          d.domain, d.scan_count, d.first_scan_at, d.last_scan_at,
          d.claimed_account_id !== null, d.claimed_account_name, d.report_url,
          d.public_scan?.outcome ?? null, d.public_scan?.platform ?? null,
          d.public_scan?.signals_present ?? null, d.public_scan?.signals_total ?? null,
          ...CSV_SIGNALS.map((k) => d.public_scan?.signals[k] ?? null),
        ]),
      ]);
    }
  };

  const th = {
    textAlign: 'left' as const,
    fontFamily: monoFont,
    fontSize: 10,
    letterSpacing: 1,
    textTransform: 'uppercase' as const,
    color: C.muted,
    padding: '6px 8px',
    borderBottom: `1px solid ${C.border}`,
    whiteSpace: 'nowrap' as const,
  };
  const td = { padding: '8px', fontSize: 12.5, color: C.body, borderBottom: `1px solid ${C.borderSoft ?? C.border}`, verticalAlign: 'top' as const };
  const chip = (active: boolean) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    fontFamily: sansFont,
    fontSize: 12,
    padding: '6px 12px',
    minHeight: 36,
    borderRadius: 3,
    cursor: 'pointer',
    background: active ? C.surface2 : 'transparent',
    color: active ? C.text : C.muted,
    border: `1px solid ${active ? C.lime : C.border}`,
  });

  const claimedCell = (id: string | null, name: string | null): JSX.Element =>
    id === null ? (
      <Pill tone="amber">unclaimed</Pill>
    ) : (
      <button
        type="button"
        onClick={() => setView('account-detail', { accountId: id })}
        style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', textAlign: 'left' }}
      >
        <Pill tone="lime">claimed</Pill>{' '}
        <span style={{ fontSize: 12, color: C.text, textDecoration: 'underline' }}>{name ?? id}</span>
      </button>
    );

  const reportCell = (url: string | null): JSX.Element =>
    url === null ? (
      <span style={{ color: C.dim }}>—</span>
    ) : (
      <a href={url} target="_blank" rel="noreferrer" style={{ color: C.text, fontSize: 12, textDecoration: 'underline' }}>
        Open
      </a>
    );

  const leads: readonly AdminLead[] = data?.leads ?? [];
  const domains: readonly AdminScannedDomain[] = data?.scanned_domains ?? [];

  return (
    <div style={{ padding: 24, maxWidth: 1100 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
        <div>
          <div style={{ fontFamily: monoFont, fontSize: 10, letterSpacing: 1.5, color: C.muted, textTransform: 'uppercase', marginBottom: 4 }}>
            Growth · Scans and signups
          </div>
          <h1 style={{ fontFamily: serifFont, fontSize: 28, color: C.text, margin: 0 }}>Leads</h1>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" onClick={() => void exportCsv()} disabled={data === null} style={chip(false)}>
            <Download size={12} /> Export CSV
          </button>
          <button type="button" onClick={() => void load()} style={chip(false)}>
            <RefreshCw size={12} /> Refresh
          </button>
        </div>
      </div>

      <Card style={{ padding: 16, marginBottom: 12 }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <button type="button" style={chip(tab === 'emails')} onClick={() => setTab('emails')}>
            Emails{data !== null ? ` (${data.leads_total})` : ''}
          </button>
          <button type="button" style={chip(tab === 'domains')} onClick={() => setTab('domains')}>
            Scanned, no email{data !== null ? ` (${data.scanned_domains_total})` : ''}
          </button>
          <span style={{ width: 1, height: 24, background: C.border, margin: '0 4px' }} />
          {(['unclaimed', 'claimed', 'all'] as const).map((s) => (
            <button key={s} type="button" style={chip(status === s)} onClick={() => setStatus(s)}>
              {s === 'all' ? 'All' : s === 'claimed' ? 'Claimed' : 'Not claimed'}
            </button>
          ))}
          <div style={{ flex: '1 1 200px', minWidth: 0 }}>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search email or domain"
              aria-label="Search email or domain"
              style={{
                width: '100%',
                fontFamily: sansFont,
                fontSize: 13,
                padding: '8px 10px',
                minHeight: 36,
                background: C.surface,
                color: C.text,
                border: `1px solid ${C.border}`,
                borderRadius: 3,
              }}
            />
          </div>
        </div>
        <div style={{ fontFamily: sansFont, fontSize: 12, color: C.muted, marginTop: 10, lineHeight: 1.5 }}>
          Claimed means someone signed in with that email and has an account. Emails are captured from
          signup and readiness-report requests. Only email people about Sill in line with the privacy policy
          and their consent.
        </div>
      </Card>

      {error !== null ? (
        <Card style={{ padding: 16, marginBottom: 12 }}>
          <div style={{ fontFamily: sansFont, fontSize: 13, color: C.coralFg }}>{error}</div>
        </Card>
      ) : null}
      {loading ? <div style={{ fontFamily: sansFont, fontSize: 13, color: C.muted }}>Loading…</div> : null}

      {flash !== null ? (
        <div style={{ fontFamily: sansFont, fontSize: 13, color: C.limeFg, marginBottom: 10 }}>{flash}</div>
      ) : null}

      {data !== null ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 10 }}>
          <span style={{ fontFamily: monoFont, fontSize: 11, color: C.muted }}>
            {selected.size} selected
          </span>
          <button
            type="button"
            disabled={selected.size === 0 || deleting}
            onClick={() => setConfirmOpen(true)}
            style={{
              ...chip(false),
              color: selected.size === 0 ? C.dim : C.coralFg,
              borderColor: selected.size === 0 ? C.border : C.coralFg,
              cursor: selected.size === 0 ? 'not-allowed' : 'pointer',
              opacity: selected.size === 0 ? 0.6 : 1,
            }}
          >
            <Trash2 size={12} /> Delete selected
          </button>
          <button
            type="button"
            disabled={selected.size === 0 || linking}
            onClick={() => void createLinks()}
            title="Make a shareable report for each selected domain. Nothing is emailed."
            style={{
              ...chip(false),
              cursor: selected.size === 0 ? 'not-allowed' : 'pointer',
              opacity: selected.size === 0 ? 0.6 : 1,
            }}
          >
            <Link2 size={12} /> {linking ? 'Creating links…' : 'Create report links'}
          </button>
          {selected.size > 0 ? (
            <button type="button" style={chip(false)} onClick={() => setSelected(new Set())}>
              Clear selection
            </button>
          ) : null}
        </div>
      ) : null}

      {!loading && data !== null && tab === 'emails' ? (
        <Card style={{ padding: 0 }}>
          {leads.length === 0 ? (
            <div style={{ padding: 16, fontFamily: sansFont, fontSize: 13, color: C.muted }}>No leads match.</div>
          ) : (
            <TableScroll minWidth={1060}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: sansFont }}>
                <thead>
                  <tr>
                    <th style={{ ...th, width: 36 }}>
                      <input
                        type="checkbox"
                        aria-label="Select all on this page"
                        checked={leads.length > 0 && leads.every((l) => selected.has(leadKey(l)))}
                        onChange={(e) =>
                          setSelected(e.target.checked ? new Set(leads.map(leadKey)) : new Set())
                        }
                      />
                    </th>
                    {['Email', 'Domain', 'Status', 'Email status', 'First seen', 'Last scan', 'Scans', 'Source', 'Report', ''].map((h) => (
                      <th key={h} style={th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {leads.map((l) => (
                    <tr key={leadKey(l)}>
                      <td style={td}>
                        <input
                          type="checkbox"
                          aria-label={`Select ${l.email}`}
                          checked={selected.has(leadKey(l))}
                          onChange={() => toggle(leadKey(l))}
                        />
                      </td>
                      <td style={{ ...td, color: C.text, fontFamily: monoFont, fontSize: 12, overflowWrap: 'anywhere', minWidth: 220 }}>{l.email}</td>
                      <td style={{ ...td, fontFamily: monoFont, fontSize: 12, overflowWrap: 'anywhere' }}>{l.domain ?? '—'}</td>
                      <td style={td}>{claimedCell(l.claimed_account_id, l.claimed_account_name)}</td>
                      <td style={td}>
                        {l.contact_status !== null
                          ? <Pill tone={CONTACT_TONE[l.contact_status]}>{l.contact_status.replace('_', ' ')}</Pill>
                          : <span style={{ color: C.dim }}>—</span>}
                      </td>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>{dateOnly(l.first_seen_at)}</td>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>{dateOnly(l.last_scan_at)}</td>
                      <td style={td}>{l.scan_count}</td>
                      <td style={{ ...td, fontFamily: monoFont, fontSize: 11 }}>{l.sources.join(', ').replace(/_/g, ' ')}</td>
                      <td style={td}>{reportCell(l.report_url)}</td>
                      <td style={td}>
                        {l.domain !== null ? (
                          <button
                            type="button"
                            disabled={l.contact_status !== null && BLOCKING.includes(l.contact_status)}
                            onClick={() => openSend(l.email, l.domain as string, false)}
                            title={l.last_staff_send_at !== null ? `Last sent ${dateOnly(l.last_staff_send_at)}` : 'Email this domain\'s readiness report'}
                            style={{ ...chip(false), minHeight: 28, padding: '3px 8px', whiteSpace: 'nowrap',
                              opacity: l.contact_status !== null && BLOCKING.includes(l.contact_status) ? 0.5 : 1 }}
                          >
                            <Mail size={12} /> Send report
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableScroll>
          )}
        </Card>
      ) : null}

      {!loading && data !== null && tab === 'domains' ? (
        <Card style={{ padding: 0 }}>
          {domains.length === 0 ? (
            <div style={{ padding: 16, fontFamily: sansFont, fontSize: 13, color: C.muted }}>No scanned domains match.</div>
          ) : (
            <TableScroll minWidth={940}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: sansFont }}>
                <thead>
                  <tr>
                    <th style={{ ...th, width: 36 }}>
                      <input
                        type="checkbox"
                        aria-label="Select all on this page"
                        checked={domains.length > 0 && domains.every((d) => selected.has(d.domain))}
                        onChange={(e) =>
                          setSelected(e.target.checked ? new Set(domains.map((d) => d.domain)) : new Set())
                        }
                      />
                    </th>
                    {['Domain', 'Status', 'Readiness', 'Platform', 'First scan', 'Last scan', 'Scans', 'Report', ''].map((h) => (
                      <th key={h} style={th}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {domains.map((d) => (
                    <tr key={d.domain}>
                      <td style={td}>
                        <input
                          type="checkbox"
                          aria-label={`Select ${d.domain}`}
                          checked={selected.has(d.domain)}
                          onChange={() => toggle(d.domain)}
                        />
                      </td>
                      <td style={{ ...td, color: C.text, fontFamily: monoFont, fontSize: 12, overflowWrap: 'anywhere' }}>{d.domain}</td>
                      <td style={td}>{claimedCell(d.claimed_account_id, d.claimed_account_name)}</td>
                      <td style={{ ...td, whiteSpace: 'nowrap' }} title={signalsTitle(d.public_scan)}>
                        {readinessLabel(d.public_scan)}
                      </td>
                      <td style={td}>{d.public_scan?.platform ?? '—'}</td>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>{dateOnly(d.first_scan_at)}</td>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>{dateOnly(d.last_scan_at)}</td>
                      <td style={td}>{d.scan_count}</td>
                      <td style={td}>{reportCell(d.report_url)}</td>
                      <td style={td}>
                        <button
                          type="button"
                          onClick={() => openSend('', d.domain, true)}
                          style={{ ...chip(false), minHeight: 28, padding: '3px 8px', whiteSpace: 'nowrap' }}
                        >
                          <Mail size={12} /> Send report
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableScroll>
          )}
        </Card>
      ) : null}

      {data !== null ? (() => {
        const total = (tab === 'emails' ? data?.leads_total ?? 0 : data?.scanned_domains_total ?? 0);
        const from = total === 0 ? 0 : offset + 1;
        const to = Math.min(offset + PAGE_SIZE, total);
        const canPrev = offset > 0;
        const canNext = offset + PAGE_SIZE < total;
        const pagerBtn = (enabled: boolean) => ({
          ...chip(false),
          color: enabled ? C.text : C.dim,
          opacity: enabled ? 1 : 0.5,
          cursor: enabled ? 'pointer' : 'not-allowed',
          minHeight: 40,
        });
        return (
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginTop: 14 }}>
            <div style={{ fontFamily: monoFont, fontSize: 11, color: C.muted, letterSpacing: 1 }}>
              {total === 0 ? '0 results' : `${from}–${to} of ${total}`}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button type="button" aria-label="Previous page" disabled={!canPrev || loading}
                onClick={() => { setSelected(new Set()); setOffset(Math.max(0, offset - PAGE_SIZE)); }} style={pagerBtn(canPrev)}>
                <ChevronLeft size={13} /> Prev
              </button>
              <button type="button" aria-label="Next page" disabled={!canNext || loading}
                onClick={() => { setSelected(new Set()); setOffset(offset + PAGE_SIZE); }} style={pagerBtn(canNext)}>
                Next <ChevronRight size={13} />
              </button>
            </div>
          </div>
        );
      })() : null}

      <ConfirmDialog
        open={sendTarget !== null}
        tone="lime"
        title={sendTarget !== null ? `Email the ${sendTarget.domain} report?` : ''}
        body={
          <div>
            <label style={{ display: 'block', fontFamily: monoFont, fontSize: 10, letterSpacing: 1, textTransform: 'uppercase', color: C.muted, marginBottom: 6 }}>
              Recipient
            </label>
            <input
              type="email"
              value={sendEmail}
              readOnly={sendTarget?.editable === false}
              onChange={(e) => setSendEmail(e.target.value)}
              placeholder="name@company.com"
              aria-label="Recipient email"
              style={{ width: '100%', fontFamily: monoFont, fontSize: 13, padding: '8px 10px', background: C.surface2, color: C.text, border: `1px solid ${C.border}`, borderRadius: 3, marginBottom: 10 }}
            />
            Sill scans the site again and emails the full report. The email carries an unsubscribe link and
            our postal address. One report per address and domain per 24 hours.
          </div>
        }
        confirmLabel={sending ? 'Sending…' : 'Send report'}
        pending={sending}
        onConfirm={() => void sendReport()}
        onCancel={() => setSendTarget(null)}
      />
      <ConfirmDialog
        open={confirmOpen}
        tone="coral"
        title={tab === 'emails'
          ? `Delete ${selected.size} lead${selected.size === 1 ? '' : 's'}?`
          : `Delete scans for ${selected.size} domain${selected.size === 1 ? '' : 's'}?`}
        body={tab === 'emails'
          ? 'Removes these emails from Leads permanently. Accounts are not touched.'
          : 'Removes the pre-signup scans of these domains permanently. Sites and their scans are not touched.'}
        confirmLabel="Delete"
        pending={deleting}
        onConfirm={() => void deleteSelected()}
        onCancel={() => setConfirmOpen(false)}
      />
    </div>
  );
}
